import { ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import LazyVideo from "../components/LazyVideo";
import { VIDEOS, POSTERS } from "../lib/media";

interface FeatureCardProps {
  video: string;
  poster?: string;
  tag: string;
  title: string;
  desc: string;
  href: string;
  delay?: number;
}

function FeatureCard({ video, poster, tag, title, desc, href, delay = 0 }: FeatureCardProps) {
  return (
    <Reveal delay={delay}>
      <article className="liquid-glass rounded-3xl overflow-hidden group h-full flex flex-col">
        <div className="relative aspect-video overflow-hidden">
          <LazyVideo className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                 src={video} poster={poster} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
        </div>
        <div className="p-6 md:p-8 flex-1 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="text-white/40 text-xs tracking-widest uppercase">{tag}</div>
            <a href={href} className="liquid-glass rounded-full p-2 text-white hover:bg-white/5 transition-colors" aria-label={title}><ArrowUpRight size={16} /></a>
          </div>
          <h3 className="font-instrument text-white text-2xl md:text-3xl mb-3 tracking-tight">{title}</h3>
          <p className="text-white/55 text-sm leading-relaxed">{desc}</p>
        </div>
      </article>
    </Reveal>
  );
}

interface ServiceRowProps { tag: string; title: string; desc: string; href: string; delay?: number; }
function ServiceRow({ tag, title, desc, href, delay = 0 }: ServiceRowProps) {
  return (
    <Reveal delay={delay}>
      <div className="liquid-glass rounded-2xl p-6 md:p-7 flex flex-col h-full group hover:bg-white/[0.02] transition-colors">
        <div className="flex items-start justify-between mb-3">
          <div className="text-white/40 text-xs tracking-widest uppercase">{tag}</div>
          <a href={href} className="text-white/40 group-hover:text-white transition-colors" aria-label={title}><ArrowUpRight size={16} /></a>
        </div>
        <h4 className="font-instrument text-white text-xl md:text-2xl mb-3 tracking-tight">{title}</h4>
        <p className="text-white/55 text-sm leading-relaxed">{desc}</p>
      </div>
    </Reveal>
  );
}

export default function Services() {
  return (
    <section id="services" className="bg-black py-28 md:py-40 px-6 overflow-hidden relative"
             style={{ backgroundImage: "radial-gradient(ellipse at center, rgba(255,255,255,0.02) 0%, transparent 60%)" }}>
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="flex items-end justify-between gap-6 mb-12 md:mb-16">
            <h2 className="font-instrument text-4xl md:text-6xl text-white tracking-tight">
              What we <em className="italic-serif text-white/50">do</em>
            </h2>
            <div className="text-white/40 text-sm tracking-widest uppercase hidden md:block">Our services</div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <FeatureCard
            video={VIDEOS.serviceA}
            poster={POSTERS.serviceA}
            tag="Strategy"
            title="AI Transformation Consultancy"
            desc="Capability assessments, roadmaps, vendor selection and hands-on enablement. We embed with your leadership so AI strategy translates into shipped systems, not slideware."
            href="/services/ai-consultancy.html"
          />
          <FeatureCard
            video={VIDEOS.serviceB}
            poster={POSTERS.serviceB}
            tag="Craft"
            title="Custom AI-Powered Products"
            desc="End-to-end product builds: discovery, design, model, code, deployment and ops. Native AI from the first line, not bolted on after the fact."
            href="/services/custom-products.html"
            delay={150}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-6 md:mt-8">
          <ServiceRow tag="Integration" title="AI Integration for Business" desc="Embed LLMs, computer vision and predictive models into your ERPs, CRMs, plant systems and field tools, where work happens." href="/services/ai-integration.html" />
          <ServiceRow tag="Audit"       title="Defence-Ready Audits"       desc="Systematic audits across data, access, supply-chain and AI risk, mapped to Essential 8, ISO 27001 and NIST CSF." href="/services/defence-audits.html" delay={80} />
          <ServiceRow tag="Governance"  title="Policy Generation"          desc="We generate, tailor and maintain the policies you need: security, AI usage, data governance and vendor risk. Kept current as standards evolve." href="/services/policy-generation.html" delay={160} />
          <ServiceRow tag="Squads"      title="Engineering as a Service"   desc="Senior squads embedded with your team. 20+ years of software DNA, two years deep on AI-native delivery. Plug in, ship, scale." href="/services/engineering-squads.html" delay={240} />
          <ServiceRow tag="Rescue"      title="AI App Rescue & Hardening"  desc="Built something fast with AI tools and not sure it's ready for real users? We audit, harden and ship your prototype to production - security, load, tenancy and data integrity included." href="/#contact" delay={320} />
          <ServiceRow tag="Support"     title="Managed Support & Ongoing Development" desc="Subscription-based support, monitoring and continued development for every system we build or inherit. A named engineer who knows your stack, available when you need them." href="/#contact" delay={400} />
        </div>
      </div>
    </section>
  );
}
